/**
 * The terminal's own identity.
 *
 * Pairing exchanges a short code for a long-lived device token (see
 * docs/ARCHITECTURE.md §7.9 and the backend's `devices` app). The token is what
 * makes a four-digit PIN acceptable: it is only ever accepted from a terminal
 * that has already been registered to the shop.
 */

import { api, ApiError, NetworkError } from "@/api/client";
import { db, getSyncMeta, updateSyncMeta } from "@/db/database";

import { offlineGrantRepository } from "./offlineGrantRepository";

interface PairResponse {
  device_token: string;
  device: {
    id: string;
    name: string;
    merchant_id: string;
    branch_id: string;
    till_id: string | null;
  };
}

const TOKEN_KEY = "smartspaza.device_token";

/**
 * Who this terminal belongs to, and who works here.
 *
 * Answered from the device token alone, because nobody has signed in yet — that
 * is the point. Returns null when the terminal is not paired, which is the
 * screen's cue to ask for a pairing code rather than to show an empty shop.
 */
export interface Terminal {
  store: string;
  terminal: string;
  staff: { staff_code: string; name: string; role: string }[];
  /** Built on the till with no signal, from what it last heard and its grants. */
  offline?: boolean;
}

const TERMINAL_KEY = "smartspaza.terminal";

export const deviceRepository = {
  /**
   * The stored device token.
   *
   * Kept in localStorage rather than IndexedDB, deliberately: it is needed by the
   * login screen before any database has been opened, and it is the one value
   * that must survive a local database being cleared — losing it would strand a
   * paired terminal with no way back in short of re-pairing.
   */
  async terminal(token: string): Promise<Terminal | null> {
    try {
      const found = await api.get<Terminal>("/auth/terminal/", {
        headers: { "X-SmartSpaza-Device-Token": token },
        authenticatedByDevice: true,
      });
      if (!found || !Array.isArray(found.staff)) return null;
      try {
        globalThis.localStorage?.setItem(TERMINAL_KEY, JSON.stringify(found));
      } catch {
        // Storage blocked; the online answer still stands.
      }
      return found;
    } catch (error) {
      // An unpaired or unknown terminal: the staff-code field, which always
      // works, and the caller deals with the pairing.
      if (!(error instanceof NetworkError)) return null;
      return this.offlineTerminal();
    }
  },

  /**
   * The sign-in screen with no signal.
   *
   * Lists exactly the people who can sign in here offline — everybody with an
   * unexpired grant on this till — rather than the whole roster, which would
   * offer tiles that then refuse. The shop and till names and each person's
   * role come from the last roster the till heard, when it has one.
   */
  async offlineTerminal(): Promise<Terminal | null> {
    let cached: Terminal | null = null;
    try {
      const raw = globalThis.localStorage?.getItem(TERMINAL_KEY);
      cached = raw ? (JSON.parse(raw) as Terminal) : null;
    } catch {
      cached = null;
    }

    const people = await offlineGrantRepository.signInable().catch(() => []);
    if (people.length === 0) return null;

    const roles = new Map(cached?.staff.map((person) => [person.staff_code.toUpperCase(), person]));
    return {
      store: cached?.store ?? "",
      terminal: cached?.terminal ?? "",
      offline: true,
      staff: people.map((person) => {
        const known = roles.get(person.staffCode);
        return {
          staff_code: person.staffCode,
          name: known?.name ?? person.displayName.split(" ")[0] ?? person.staffCode,
          role: known?.role ?? "",
        };
      }),
    };
  },

  /**
   * When this terminal last reached the server, or null if it never has.
   *
   * Exposed here rather than read from the database in a component: the local
   * store is the repository layer's business, and an ESLint rule enforces it.
   */
  async lastSyncAt(): Promise<string | null> {
    try {
      return (await getSyncMeta()).lastSuccessfulSyncAt;
    } catch {
      return null;
    }
  },

  getToken(): Promise<string | null> {
    // Async by contract even though localStorage is synchronous: Phase 5 may move
    // this behind an encrypted store, and every caller already awaits it.
    try {
      return Promise.resolve(globalThis.localStorage?.getItem(TOKEN_KEY) ?? null);
    } catch {
      return Promise.resolve(null);
    }
  },

  /**
   * Tell the server this terminal is alive, and how much it still owes.
   *
   * Authenticated by the **device token, not a user**, because the moment worth
   * knowing about is a shop that has closed for the night with sales still
   * queued — and nobody is signed in then. A queue depth that never falls is the
   * signal; a terminal that stops beating altogether is the other one.
   *
   * Failure is swallowed. A heartbeat that could not be sent is not something to
   * put in front of a cashier, and the next one is a minute away.
   *
   * A **401** is the exception, and it is not silent: the server is saying it
   * does not recognise this terminal. Keeping the token would leave the device
   * beating uselessly once a minute forever and offering a PIN keypad that can
   * never succeed. `authenticatedByDevice` also stops the shared client
   * treating that 401 as the user's session expiring — which signed a
   * back-office user out every minute on a terminal whose pairing had been
   * revoked.
   */
  async heartbeat(): Promise<void> {
    const token = await this.getToken();
    if (!token) return;

    try {
      await api.post(
        "/devices/heartbeat/",
        {
          app_version: __APP_VERSION__,
          pending_operations: await db.pendingOperationCount(),
        },
        {
          headers: { "X-SmartSpaza-Device-Token": token },
          timeoutMs: 10_000,
          authenticatedByDevice: true,
        },
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) this.forgetToken();
      // Anything else is deliberately silent — see above.
    }
  },

  /**
   * Forget a token the server no longer recognises.
   *
   * Distinct from `unpair`, which is a person's decision and refuses while work
   * is queued. This is the server telling us the token is meaningless — keeping
   * it would leave the terminal insisting it is paired and offering a keypad
   * that cannot succeed.
   *
   * Queued work is deliberately **not** checked here, because it is not at risk:
   * the outbox is keyed to the merchant's session, not the device token, and
   * pairing again restores the terminal without touching it.
   */
  forgetToken(): void {
    globalThis.localStorage?.removeItem(TOKEN_KEY);
    // Offline sign-in is only ever trusted on a paired till. A till the server
    // no longer recognises must stop accepting PINs on its own.
    void offlineGrantRepository.forgetAll().catch(() => {});
  },

  /** Exchange a pairing code for a token. Returns the device it belongs to. */
  async pair(pairingCode: string): Promise<PairResponse["device"]> {
    const body = await api.post<PairResponse>("/devices/pair/", {
      pairing_code: pairingCode,
      platform: globalThis.navigator?.userAgent?.slice(0, 100) ?? "",
    });

    globalThis.localStorage?.setItem(TOKEN_KEY, body.device_token);
    await updateSyncMeta({ deviceId: body.device.id });

    return body.device;
  },

  /**
   * Forget this terminal's pairing.
   *
   * Guarded: un-pairing a device with unsynced sales would strand them, because
   * nothing else can authenticate their upload.
   */
  async unpair(): Promise<{ unpaired: boolean; pendingOperations: number }> {
    const pending = await db.pendingOperationCount();
    if (pending > 0) return { unpaired: false, pendingOperations: pending };

    globalThis.localStorage?.removeItem(TOKEN_KEY);
    await updateSyncMeta({ deviceId: null });
    return { unpaired: true, pendingOperations: 0 };
  },

  async deviceId(): Promise<string | null> {
    return (await getSyncMeta()).deviceId;
  },
};
